import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  PiCheckCircleDuotone as SavedIcon,
  PiMapPinAreaDuotone as ZoneIcon,
  PiPlusBold as Plus,
  PiTrashDuotone as Trash2,
  PiWarningDuotone as WarningIcon,
} from "react-icons/pi";
import { ActionIcon, Alert, Badge, Button, Input, Table, Text } from "rizzui";
import { GRID_TABLE } from "../table/table-view";
import { EmptyState, WidgetCard } from "@steelyard/theme";
import { CardTitle } from "../detail-parts";

type ZoneRow = { pincodeFrom: string; pincodeTo: string; charge: string };
type ZonesFormValues = { zones: ZoneRow[] };
type Zone = { pincodeFrom: string; pincodeTo: string; charge: number };

const toRows = (zones: readonly { pincodeFrom: string; pincodeTo: string; charge: number | string }[]) =>
  zones.map((zone) => ({ pincodeFrom: zone.pincodeFrom, pincodeTo: zone.pincodeTo, charge: String(zone.charge) }));

/**
 * The delivery rate card. Ported from apps/admin/components/delivery-zone-editor.tsx; `PUT` still
 * replaces the whole table in one call.
 *
 * Repeatable rows are react-hook-form's `useFieldArray`, the pattern vendor uses for its own
 * "Location Based Shipping" rows:
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/product/create-edit/shipping-info.tsx
 * laid out as a line table the way the order's own product lines are:
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-products/order-products.tsx
 * — a rizzui `Table` of inputs, not a `TableView`: paging it would unmount the inputs on the pages
 * not shown (CLAUDE.md, the credit-note line table).
 *
 * What changed for safety, not looks:
 * - Unmatched pincodes sit above the table with one-click "add a zone" — a new row pre-filled with
 *   that pincode as both ends and the charge left for a person to type. Nothing decides whether a
 *   pincode is covered; that stays the API's answer on the next load.
 * - Saving is two steps. A validated submit asks "replace the table with N zones?" before `onSave`;
 *   any edit after that withdraws the question, so a confirmation never covers rows it did not show.
 *
 * Validation is unchanged: a fully blank row is dropped (and said so in a toast), a partly filled
 * row gets per-field inline errors, a charge must be a non-negative number.
 */
export function DeliveryZoneEditor({
  initialZones,
  unmatchedPincodes = [],
  onSave,
  busy,
}: {
  initialZones: readonly { pincodeFrom: string; pincodeTo: string; charge: number | string }[];
  unmatchedPincodes?: readonly string[];
  onSave: (zones: Zone[]) => void;
  busy: boolean;
}) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<ZonesFormValues>({ defaultValues: { zones: toRows(initialZones) } });
  const { fields, append, remove } = useFieldArray({ control, name: "zones" });
  const [pending, setPending] = useState<Zone[] | null>(null);
  const [queued, setQueued] = useState<Set<string>>(new Set());

  // Resyncs the rows after a save: `initialZones` is what the server now holds, and without this a
  // blank scratch row left over from "Add zone" would stay visible under a "Saved" banner.
  useEffect(() => {
    reset({ zones: toRows(initialZones) });
    setQueued(new Set());
    setPending(null);
  }, [initialZones, reset]);

  // Any edit after "Review" withdraws the confirmation — it must never cover rows it did not show.
  useEffect(() => {
    const subscription = watch(() => setPending(null));
    return () => subscription.unsubscribe();
  }, [watch]);

  const addZone = (pincode?: string) => {
    append({ pincodeFrom: pincode ?? "", pincodeTo: pincode ?? "", charge: "" }, { focusName: `zones.${fields.length}.${pincode ? "charge" : "pincodeFrom"}` });
    if (pincode) setQueued((current) => new Set(current).add(pincode));
  };

  const review = handleSubmit((values) => {
    clearErrors();
    let hasError = false;
    let blankRowsDropped = 0;
    const zones: Zone[] = [];

    values.zones.forEach((row, index) => {
      const pincodeFrom = row.pincodeFrom.trim();
      const pincodeTo = row.pincodeTo.trim();
      const chargeRaw = row.charge.trim();
      if (!pincodeFrom && !pincodeTo && !chargeRaw) {
        blankRowsDropped += 1; // an unfilled scratch row — not saved, but never silently so (see below)
        return;
      }

      const charge = Number(chargeRaw);
      const pincode = /^\d{6}$/;
      if (!pincode.test(pincodeFrom)) {
        setError(`zones.${index}.pincodeFrom`, { message: pincodeFrom ? "Enter a 6-digit pincode" : "Required" });
        hasError = true;
      }
      if (!pincode.test(pincodeTo)) {
        setError(`zones.${index}.pincodeTo`, { message: pincodeTo ? "Enter a 6-digit pincode" : "Required" });
        hasError = true;
      } else if (pincode.test(pincodeFrom) && pincodeTo < pincodeFrom) {
        setError(`zones.${index}.pincodeTo`, { message: "Must not be before the From pincode" });
        hasError = true;
      }
      if (!chargeRaw || !Number.isFinite(charge) || charge < 0) {
        setError(`zones.${index}.charge`, { message: "Enter a valid charge" });
        hasError = true;
      }
      zones.push({ pincodeFrom, pincodeTo, charge });
    });

    if (hasError) return;
    if (blankRowsDropped > 0) {
      toast(blankRowsDropped === 1 ? "One empty row was left out — nothing was typed into it." : `${blankRowsDropped} empty rows were left out — nothing was typed into them.`);
    }
    setPending(zones);
  });

  return (
    <div className="flex flex-col gap-6">
      {unmatchedPincodes.length > 0 ? (
        <Alert color="warning" variant="flat" bar icon={<WarningIcon aria-hidden className="size-5" />}>
          <Text className="font-semibold text-orange-dark">
            {unmatchedPincodes.length === 1 ? "One pincode matches no zone" : `${unmatchedPincodes.length} pincodes match no zone`}
          </Text>
          <Text className="mt-0.5 text-sm">
            They are being charged the configured fallback. Add a zone for each one below, type its charge, then review and replace the table.
          </Text>
          <ul className="mt-3 flex flex-wrap gap-2">
            {unmatchedPincodes.map((pincode) => {
              const added = queued.has(pincode);
              return (
                <li key={pincode}>
                  <Button
                    type="button"
                    size="sm"
                    variant={added ? "flat" : "outline"}
                    disabled={added}
                    onClick={() => addZone(pincode)}
                    className="gap-1.5 font-mono"
                    aria-label={added ? `A row for ${pincode} was added below` : `Add a zone row for pincode ${pincode}`}
                  >
                    {added ? <SavedIcon aria-hidden className="size-4 text-green-dark" /> : <Plus aria-hidden className="size-3" />}
                    {pincode}
                  </Button>
                </li>
              );
            })}
          </ul>
        </Alert>
      ) : null}

      <WidgetCard
        rounded="xl"
        className="overflow-hidden p-0 lg:p-0"
        headerClassName="items-center px-5 pb-4 pt-5 lg:px-7 lg:pt-6"
        title={
          <CardTitle icon={<ZoneIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
            <span className="flex items-center gap-2">
              Rate card
              <Badge variant="flat" color="info" rounded="pill" size="sm">
                {fields.length}
              </Badge>
            </span>
          </CardTitle>
        }
        action={
          isDirty ? (
            <Badge variant="flat" color="warning" rounded="pill">
              Unsaved changes
            </Badge>
          ) : (
            <Badge variant="flat" color="success" rounded="pill">
              Matches what is saved
            </Badge>
          )
        }
      >
        <form onSubmit={review} noValidate>
          {fields.length === 0 ? (
            <div className="px-5 pb-6 lg:px-7">
              <EmptyState
                title="No zones configured"
                action={
                  <Button type="button" onClick={() => addZone()}>
                    <Plus aria-hidden className="me-1.5 size-4" />
                    Add the first zone
                  </Button>
                }
              >
                Every checkout is charged the configured fallback until a zone matches.
              </EmptyState>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table variant="modern" className={`w-full min-w-[620px] ${GRID_TABLE}`}>
                <caption className="sr-only">Delivery zones: pincode range and delivery charge</caption>
                <Table.Header>
                  <Table.Row className="hover:bg-transparent">
                    <Table.Head className="w-12 ps-5 lg:ps-7">#</Table.Head>
                    <Table.Head>From pincode</Table.Head>
                    <Table.Head>To pincode</Table.Head>
                    <Table.Head>Delivery charge</Table.Head>
                    <Table.Head className="w-16 pe-5 lg:pe-7">
                      <span className="sr-only">Remove</span>
                    </Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {fields.map((item, index) => (
                    <Table.Row key={item.id} className="align-top transition-colors hover:bg-gray-50 dark:hover:bg-gray-100/40">
                      <Table.Cell className="py-3 ps-5 lg:ps-7">
                        <Badge variant="flat" color="info" rounded="md" size="sm" className="mt-2 tabular-nums">
                          {index + 1}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className="py-3">
                        <Input
                          aria-label={`Row ${index + 1}: from pincode`}
                          inputMode="numeric"
                          size="sm"
                          inputClassName="font-mono"
                          {...register(`zones.${index}.pincodeFrom`)}
                          error={errors.zones?.[index]?.pincodeFrom?.message}
                        />
                      </Table.Cell>
                      <Table.Cell className="py-3">
                        <Input
                          aria-label={`Row ${index + 1}: to pincode`}
                          inputMode="numeric"
                          size="sm"
                          inputClassName="font-mono"
                          {...register(`zones.${index}.pincodeTo`)}
                          error={errors.zones?.[index]?.pincodeTo?.message}
                        />
                      </Table.Cell>
                      <Table.Cell className="py-3">
                        <Input
                          aria-label={`Row ${index + 1}: delivery charge`}
                          inputMode="decimal"
                          min="0"
                          size="sm"
                          prefix={<span className="text-sm text-gray-500">₹</span>}
                          inputClassName="font-medium tabular-nums"
                          {...register(`zones.${index}.charge`)}
                          error={errors.zones?.[index]?.charge?.message}
                        />
                      </Table.Cell>
                      <Table.Cell className="py-3 pe-5 text-right lg:pe-7">
                        {/* Every row can go, the last one too — an empty table is a valid state (the fallback charge applies). */}
                        {(
                          <ActionIcon
                            type="button"
                            variant="text"
                            size="sm"
                            onClick={() => remove(index)}
                            className="mt-0.5 text-gray-500 hover:bg-red-lighter hover:text-red-dark"
                            aria-label={`Remove row ${index + 1}`}
                          >
                            <Trash2 aria-hidden className="size-4" />
                          </ActionIcon>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}

          {pending ? (
            <div className="border-t border-muted px-5 py-4 lg:px-7" role="status">
              <Alert color="warning" variant="flat" bar icon={<WarningIcon aria-hidden className="size-5" />}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <Text className="font-semibold text-orange-dark">
                      {pending.length === 0
                        ? "Remove every zone?"
                        : `Replace the whole table with ${pending.length === 1 ? "this 1 zone" : `these ${pending.length} zones`}?`}
                    </Text>
                    <Text className="mt-0.5 text-sm">The next checkout is charged from exactly these rows. Any zone not listed here is removed.</Text>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="text" size="sm" onClick={() => setPending(null)}>
                      Keep editing
                    </Button>
                    <Button type="button" size="sm" isLoading={busy} onClick={() => onSave(pending)}>
                      Yes, replace the table
                    </Button>
                  </div>
                </div>
              </Alert>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-muted bg-gray-50 px-5 py-4 lg:px-7 dark:bg-gray-100/40">
            <Button type="button" variant="outline" size="sm" onClick={() => addZone()}>
              <Plus aria-hidden className="me-1.5 size-4" />
              Add zone
            </Button>
            <div className="flex flex-wrap items-center gap-3">
              <Text className="text-xs text-gray-500">Saving replaces the whole table with exactly these rows.</Text>
              {isDirty ? (
                <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => reset({ zones: toRows(initialZones) })}>
                  Discard changes
                </Button>
              ) : null}
              <Button type="submit" size="sm" disabled={busy || pending !== null}>
                Review and replace
              </Button>
            </div>
          </div>
        </form>
      </WidgetCard>
    </div>
  );
}
